import { VgApiResult, vgApiRequest } from "./vg-api.service";

/**
 * Facilities are what a crematorium books: cremators, chapels, rooms. In Core each one
 * is a resource with its own slot grid, and a booking takes one of its free slots.
 *
 * Listing facilities reuses readOrgResources in config-assignments.service, which is
 * also what assigns them to booking configurations.
 */

/** ENUM_SYS_Resource_Time_Allocation_Type.StaticSlots: bookings sit on a fixed grid. */
export const TIME_ALLOCATION_STATIC_SLOTS = 2;
/** Enum_SYS_BookingOverlap.NoOverlapping: a cremator or chapel holds one booking at a time. */
export const BOOKING_NO_OVERLAP = 2;

/** Measurement_Unit_Type.m. */
const MEASUREMENT_UNIT_METRES = 4;

const MS_PER_MINUTE = 60 * 1000;
const MS_PER_DAY = 24 * 60 * MS_PER_MINUTE;

export type NewFacility = {
  orgId: number;
  creatorId: number;
  name: string;
  /** Length of one bookable slot. A cremation is commonly booked in 60 or 90 minutes. */
  slotMinutes: number;
  /** The provider's pin. Core works out the facility's time zone from it. */
  latitude: number | null;
  longitude: number | null;
};

export type FreeTime = {
  resourceId: number;
  /** ISO 8601, as the API returns it. */
  start: string;
  end: string;
};

/** Why a facility cannot be created, or null. */
export const validateFacility = (f: NewFacility): string | null => {
  if (!f.orgId || f.orgId <= 0) return "Choose an active provider first.";
  if (!f.name.trim()) return "Give the facility a name, such as Cremator 1 or Main chapel.";
  if (!Number.isFinite(f.slotMinutes) || f.slotMinutes < 15 || f.slotMinutes > 8 * 60)
    return "A slot is between 15 minutes and 8 hours.";
  if (f.latitude == null || f.longitude == null)
    return "Set your location on your provider page first; the facility's time zone comes from it.";
  return null;
};

export const createFacility = async (
  accessToken: string,
  f: NewFacility
): Promise<VgApiResult<unknown>> => {
  const invalid = validateFacility(f);
  if (invalid) return { data: null, error: { message: invalid } };
  const slotMs = Math.round(f.slotMinutes) * MS_PER_MINUTE;
  return vgApiRequest(accessToken, "Create_Resource", {
    orgId: f.orgId,
    creatorId: f.creatorId,
    resourceName: f.name.trim(),
    slotDuration: slotMs,
    // Core takes no time zone on create: it looks it up from the location.
    timeZoneIANA: "",
    latitude: f.latitude,
    longitude: f.longitude,
    // 0 is an organisation's resource; Core's default of -1 is refused.
    userId: 0,
    timeAllocationType: TIME_ALLOCATION_STATIC_SLOTS,
    allowsOverlaps: BOOKING_NO_OVERLAP,
    // One booking is at most a whole day; book up to a year ahead; a closure at most two
    // weeks, the longest Core allows.
    maxAppointmentDuration: MS_PER_DAY,
    maxAppointmentFutureTimeInMs: 365 * MS_PER_DAY,
    maxExceptionDuration: 14 * MS_PER_DAY,
    maxDailyUserSlots: 0,
    // Core sizes every resource and refuses its default of -1 with no unit. A facility's
    // size is not used, so none, in metres.
    widthValue: 0,
    heightValue: 0,
    depthValue: 0,
    widthUnitType: MEASUREMENT_UNIT_METRES,
    heightUnitType: MEASUREMENT_UNIT_METRES,
    depthUnitType: MEASUREMENT_UNIT_METRES,
  });
};

export const deleteFacility = async (
  accessToken: string,
  orgId: number,
  resourceId: number
): Promise<VgApiResult<unknown>> =>
  vgApiRequest(accessToken, "Delete_Resource", { orgId, resourceId });

export type Closure = {
  orgId: number;
  creatorId: number;
  resourceIds: number[];
  title: string;
  /** Local wall time from a datetime-local input; sent as UTC. */
  start: string;
  end: string;
  timeZoneIANA: string;
};

export const validateClosure = (c: Closure): string | null => {
  if (c.resourceIds.length === 0) return "Choose which facility is closed.";
  if (!c.title.trim()) return "Say why it is closed, for example Maintenance.";
  const start = new Date(c.start).getTime();
  const end = new Date(c.end).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end)) return "Give a start and an end.";
  if (end <= start) return "The closure must end after it starts.";
  return null;
};

/** Takes facilities out of use for a while (maintenance, a public holiday). Core calls this an exception. */
export const closeFacility = async (
  accessToken: string,
  c: Closure
): Promise<VgApiResult<unknown>> => {
  const invalid = validateClosure(c);
  if (invalid) return { data: null, error: { message: invalid } };
  const start = new Date(c.start);
  const end = new Date(c.end);
  return vgApiRequest(accessToken, "Create_Exception", {
    orgId: c.orgId,
    creatorId: c.creatorId,
    exceptionTitle: c.title.trim(),
    resourceIdList: c.resourceIds,
    calendarIdList: [],
    repeatRuleOptions: [],
    start: start.toISOString(),
    end: end.toISOString(),
    durationMilliseconds: end.getTime() - start.getTime(),
    timeZoneIANA: c.timeZoneIANA,
  });
};

type FreeSlotRow = Record<string, unknown>;

/** The facility's free slots that can be booked, between two moments. */
export const readFreeTimes = async (
  accessToken: string,
  orgId: number,
  resourceId: number,
  from: Date,
  to: Date
): Promise<VgApiResult<FreeTime[]>> => {
  const { data, error } = await vgApiRequest<Record<string, unknown>>(
    accessToken,
    "Read_Resource_FreeSlots_Between_DateTime",
    { orgId, resourceId, start: from.toISOString(), end: to.toISOString() }
  );
  if (error) return { data: null, error };
  const rows = ((data?.ListOfFreeSlots ?? data?.listOfFreeSlots ?? []) as FreeSlotRow[]) || [];
  const times = rows
    .filter((r) => (r.bookableSlot ?? r.BookableSlot ?? true) !== false)
    .map((r) => ({
      resourceId: Number(r.resourceId ?? r.ResourceId ?? resourceId),
      start: String(r.start ?? r.Start ?? ""),
      end: String(r.end ?? r.End ?? ""),
    }))
    .filter((t) => t.start && t.end)
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  return { data: times, error: null };
};

/** The browser's time zone, as a default for a new facility. */
export const browserTimeZone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Stockholm";
